import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
    fetchContactPreferences,
    upsertContactPreference,
    deleteContactPreference
} from '../services/api'
import { useAuth } from '../composables/useAuth'

interface ContactPreferenceEntry {
    supplierEmployeeId: string
    isFavorite: boolean
    notes: string | null
    contactName: string
    contactEmail: string
    contactRole: string | null
    contactPhone: string | null
    supplierId: string
    supplierName: string
    supplierSlug: string
    supplierLogoUrl: string | null
}

export const useContactPreferencesStore = defineStore('contactPreferences', () => {
    const preferences = ref<Map<string, ContactPreferenceEntry>>(new Map())
    const loaded = ref(false)
    const loading = ref(false)

    const { getAccessTokenSilently } = useAuth()

    const isFavorite = (contactId: string) => preferences.value.get(contactId)?.isFavorite ?? false

    const favorites = computed(() => {
        const result: Array<{ contactId: string } & ContactPreferenceEntry> = []
        preferences.value.forEach((pref, contactId) => {
            if (pref.isFavorite) result.push({ contactId, ...pref })
        })
        return result
    })

    const favoritesBySupplier = computed(() => {
        const groups: Record<string, { supplierName: string; supplierSlug: string; supplierLogoUrl: string | null; contacts: Array<ContactPreferenceEntry & { contactId: string }> }> = {}
        preferences.value.forEach((pref, contactId) => {
            if (!pref.isFavorite) return
            if (!groups[pref.supplierId]) {
                groups[pref.supplierId] = {
                    supplierName: pref.supplierName,
                    supplierSlug: pref.supplierSlug,
                    supplierLogoUrl: pref.supplierLogoUrl,
                    contacts: []
                }
            }
            groups[pref.supplierId]!.contacts.push({ contactId, ...pref })
        })
        return groups
    })

    async function load() {
        if (loading.value) return
        loading.value = true
        try {
            const token = await getAccessTokenSilently()
            const data = await fetchContactPreferences(token)
            const newMap = new Map<string, ContactPreferenceEntry>()
            for (const p of data.preferences) {
                newMap.set(p.supplier_employee_id, {
                    supplierEmployeeId: p.supplier_employee_id,
                    isFavorite: p.is_favorite,
                    notes: p.notes,
                    contactName: p.contact_name,
                    contactEmail: p.contact_email,
                    contactRole: p.contact_role,
                    contactPhone: p.contact_phone,
                    supplierId: p.supplier_id,
                    supplierName: p.supplier_name,
                    supplierSlug: p.supplier_slug,
                    supplierLogoUrl: p.supplier_logo_url
                })
            }
            preferences.value = newMap
            loaded.value = true
        } catch (e) {
            console.error('Failed to load contact preferences:', e)
        } finally {
            loading.value = false
        }
    }

    async function toggleFavorite(contactId: string) {
        const current = isFavorite(contactId)
        const newValue = !current

        // Optimistic update
        const existing = preferences.value.get(contactId)
        if (existing) {
            existing.isFavorite = newValue
            preferences.value = new Map(preferences.value)
        }

        try {
            const token = await getAccessTokenSilently()
            const result = await upsertContactPreference(contactId, { is_favorite: newValue }, token)
            const pref = result.preference
            preferences.value.set(contactId, {
                supplierEmployeeId: pref.supplier_employee_id,
                isFavorite: pref.is_favorite,
                notes: pref.notes,
                contactName: pref.contact_name,
                contactEmail: pref.contact_email,
                contactRole: pref.contact_role,
                contactPhone: pref.contact_phone,
                supplierId: pref.supplier_id,
                supplierName: pref.supplier_name,
                supplierSlug: pref.supplier_slug,
                supplierLogoUrl: pref.supplier_logo_url
            })
            preferences.value = new Map(preferences.value)
        } catch (e) {
            // Revert
            if (existing) {
                existing.isFavorite = current
                preferences.value = new Map(preferences.value)
            }
            console.error('Failed to toggle contact favorite:', e)
        }

        // Cleanup if not favorite and no notes
        const pref = preferences.value.get(contactId)
        if (pref && !pref.isFavorite && !pref.notes) {
            try {
                const token = await getAccessTokenSilently()
                await deleteContactPreference(contactId, token)
                preferences.value.delete(contactId)
                preferences.value = new Map(preferences.value)
            } catch {
                // Non-critical
            }
        }
    }

    return {
        preferences,
        loaded,
        loading,
        isFavorite,
        favorites,
        favoritesBySupplier,
        load,
        toggleFavorite
    }
})
